<script>
import { ref } from 'vue';
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useAI } from 'dashboard/composables/useAI';
import { useAlert } from 'dashboard/composables';
import AICTAModal from './AICTAModal.vue';
import AIAssistanceModal from './AIAssistanceModal.vue';
import { CMD_AI_ASSIST } from 'dashboard/helper/commandbar/events';
import AIAssistanceCTAButton from './AIAssistanceCTAButton.vue';
import { emitter } from 'shared/helpers/mitt';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConversationAPI from 'dashboard/api/conversations';

export default {
  components: {
    NextButton,
    AIAssistanceModal,
    AICTAModal,
    AIAssistanceCTAButton,
  },
  props: {
    conversationId: {
      type: Number,
      default: null,
    },
  },
  emits: ['replaceText'],
  setup(props, { emit }) {
    const { uiSettings, updateUISettings } = useUISettings();

    const { isAIIntegrationEnabled, draftMessage, recordAnalytics } = useAI();

    const { isAdmin } = useAdmin();

    const initialMessage = ref('');

    const initializeMessage = draftMsg => {
      initialMessage.value = draftMsg;
    };
    const keyboardEvents = {
      '$mod+KeyZ': {
        action: () => {
          if (initialMessage.value) {
            emit('replaceText', initialMessage.value);
            initialMessage.value = '';
          }
        },
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      uiSettings,
      updateUISettings,
      isAdmin,
      initialMessage,
      initializeMessage,
      recordAnalytics,
      isAIIntegrationEnabled,
      draftMessage,
    };
  },
  data: () => ({
    showAIAssistanceModal: false,
    showAICtaModal: false,
    aiOption: '',
    conversationMode: 'unknown',
    isSwitchingMode: false,
  }),
  computed: {
    ...mapGetters({
      isAChatwootInstance: 'globalConfig/isAChatwootInstance',
    }),
    isAICTAModalDismissed() {
      return this.uiSettings.is_open_ai_cta_modal_dismissed;
    },
    // Display a AI CTA button for admins if the AI integration has not been added yet and the AI assistance modal has not been dismissed.
    shouldShowAIAssistCTAButtonForAdmin() {
      return (
        this.isAdmin &&
        !this.isAIIntegrationEnabled &&
        !this.isAICTAModalDismissed &&
        this.isAChatwootInstance
      );
    },
    // Display a AI CTA button for agents and other admins who have not yet opened the AI assistance modal.
    shouldShowAIAssistCTAButton() {
      return this.isAIIntegrationEnabled && !this.isAICTAModalDismissed;
    },
    modeBadgeLabel() {
      return this.$t(
        `INTEGRATION_SETTINGS.OPEN_AI.MODE_STATUS.${this.conversationMode.toUpperCase()}`
      );
    },
    modeBadgeClass() {
      return {
        'mode-badge-ai': this.conversationMode === 'ai',
        'mode-badge-human': this.conversationMode === 'human',
        'mode-badge-unknown': this.conversationMode === 'unknown',
      };
    },
  },
  watch: {
    conversationId: {
      immediate: true,
      handler() {
        this.fetchConversationMode();
      },
    },
  },

  mounted() {
    emitter.on(CMD_AI_ASSIST, this.onAIAssist);
    this.initializeMessage(this.draftMessage);
  },
  unmounted() {
    emitter.off(CMD_AI_ASSIST, this.onAIAssist);
  },

  methods: {
    hideAIAssistanceModal() {
      this.recordAnalytics('DISMISS_AI_SUGGESTION', {
        aiOption: this.aiOption,
      });
      this.showAIAssistanceModal = false;
    },
    openAIAssist() {
      // Dismiss the CTA modal if it is not dismissed
      if (!this.isAICTAModalDismissed) {
        this.updateUISettings({
          is_open_ai_cta_modal_dismissed: true,
        });
      }
      this.initializeMessage(this.draftMessage);
      const ninja = document.querySelector('ninja-keys');
      ninja.open({ parent: 'ai_assist' });
    },
    hideAICtaModal() {
      this.showAICtaModal = false;
    },
    openAICta() {
      this.showAICtaModal = true;
    },
    async fetchConversationMode() {
      if (!this.conversationId || !this.isAIIntegrationEnabled) {
        this.conversationMode = 'unknown';
        return;
      }

      try {
        const response = await ConversationAPI.getAIModeStatus(
          this.conversationId
        );
        const mode = response?.data?.mode;
        this.conversationMode = ['ai', 'human'].includes(mode)
          ? mode
          : 'unknown';
      } catch (error) {
        this.conversationMode = 'unknown';
      }
    },
    async switchConversationMode(mode) {
      if (!this.conversationId || this.isSwitchingMode) {
        return;
      }

      this.isSwitchingMode = true;
      try {
        await ConversationAPI.toggleAIMode(this.conversationId, mode);
        this.conversationMode = mode;
        const successKey =
          mode === 'human'
            ? 'INTEGRATION_SETTINGS.OPEN_AI.MODE_SWITCH.SUCCESS_HUMAN'
            : 'INTEGRATION_SETTINGS.OPEN_AI.MODE_SWITCH.SUCCESS';
        useAlert(this.$t(successKey));
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error_message ||
          error?.response?.data?.error ||
          this.$t(
            mode === 'human'
              ? 'INTEGRATION_SETTINGS.OPEN_AI.MODE_SWITCH.ERROR_HUMAN'
              : 'INTEGRATION_SETTINGS.OPEN_AI.MODE_SWITCH.ERROR'
          );
        useAlert(errorMessage);
      } finally {
        this.isSwitchingMode = false;
      }
    },
    onAIAssist(option) {
      if (option === 'switch_to_ai_mode') {
        this.switchConversationMode('ai');
        return;
      }
      if (option === 'switch_to_human_mode') {
        this.switchConversationMode('human');
        return;
      }
      this.aiOption = option;
      this.showAIAssistanceModal = true;
    },
    insertText(message) {
      this.$emit('replaceText', message);
    },
  },
};
</script>

<template>
  <div>
    <div v-if="isAIIntegrationEnabled" class="relative">
      <span
        class="mode-badge"
        :class="modeBadgeClass"
        :title="$t('INTEGRATION_SETTINGS.OPEN_AI.MODE_STATUS_LABEL')"
      >
        {{ modeBadgeLabel }}
      </span>
      <AIAssistanceCTAButton
        v-if="shouldShowAIAssistCTAButton"
        @open="openAIAssist"
      />
      <NextButton
        v-else
        v-tooltip.top-end="$t('INTEGRATION_SETTINGS.OPEN_AI.AI_ASSIST')"
        icon="i-ph-magic-wand"
        slate
        faded
        sm
        @click="openAIAssist"
      />
      <woot-modal
        v-model:show="showAIAssistanceModal"
        :on-close="hideAIAssistanceModal"
      >
        <AIAssistanceModal
          :ai-option="aiOption"
          @apply-text="insertText"
          @close="hideAIAssistanceModal"
        />
      </woot-modal>
    </div>
    <div v-else-if="shouldShowAIAssistCTAButtonForAdmin" class="relative">
      <AIAssistanceCTAButton @click="openAICta" />
      <woot-modal v-model:show="showAICtaModal" :on-close="hideAICtaModal">
        <AICTAModal @close="hideAICtaModal" />
      </woot-modal>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.mode-badge {
  @apply inline-flex items-center px-2 py-1 mr-2 text-xs rounded-md border;
}

.mode-badge-ai {
  @apply text-n-teal-11 border-n-teal-6 bg-n-teal-2;
}

.mode-badge-human {
  @apply text-n-amber-11 border-n-amber-6 bg-n-amber-2;
}

.mode-badge-unknown {
  @apply text-n-slate-11 border-n-slate-6 bg-n-slate-2;
}
</style>
